import React, { useState } from 'react';
import {
  LayoutDashboard, ShoppingCart, Users, TrendingUp, Bell, Search,
  ArrowUpRight, ArrowDownRight, Filter, MoreHorizontal, Circle,
  CheckCircle2, Clock, ChevronRight, Zap, Package, MapPin, Star,
  CreditCard, Smartphone, Globe, RefreshCw, Download, Plus
} from 'lucide-react';

interface MacBookDeviceFrameProps {
  title?: string;
  autoScroll?: boolean;
}

const ORDERS = [
  { id: '#48291', customer: 'Ana Beatriz M.', items: 'Bruto Bacon Triple × 2', channel: 'PWA App', value: 'R$ 77,80', status: 'Entregue', statusColor: 'text-emerald-600 bg-emerald-50', time: '14:32' },
  { id: '#48290', customer: 'Carlos Eduardo S.', items: 'Smash Double Cheese × 1', channel: 'WhatsApp', value: 'R$ 29,90', status: 'Em preparo', statusColor: 'text-amber-600 bg-amber-50', time: '14:28' },
  { id: '#48289', customer: 'Fernanda Lima', items: 'Combo Família × 1', channel: 'Google Maps', value: 'R$ 89,90', status: 'Entregue', statusColor: 'text-emerald-600 bg-emerald-50', time: '14:15' },
  { id: '#48288', customer: 'Rafael Torres', items: 'Fritas Rústicas × 3', channel: 'PWA App', value: 'R$ 56,70', status: 'A caminho', statusColor: 'text-blue-600 bg-blue-50', time: '14:09' },
  { id: '#48287', customer: 'Juliana Reis', items: 'Bruto Bacon Triple × 1', channel: 'WhatsApp', value: 'R$ 38,90', status: 'Entregue', statusColor: 'text-emerald-600 bg-emerald-50', time: '13:55' },
];

const CHART_BARS = [42, 68, 55, 90, 73, 88, 95, 65, 78, 85, 92, 76];
const CHART_LABELS = ['08h', '09h', '10h', '11h', '12h', '13h', '14h', '15h', '16h', '17h', '18h', '19h'];

export const MacBookDeviceFrame: React.FC<MacBookDeviceFrameProps> = ({
  title = "Genesis 360° Hub",
  autoScroll = true
}) => {
  const [activeNav, setActiveNav] = useState('dashboard');

  const navItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Painel Geral' },
    { id: 'orders', icon: ShoppingCart, label: 'Pedidos PWA' },
    { id: 'customers', icon: Users, label: 'Clientes' },
    { id: 'analytics', icon: TrendingUp, label: 'Analíticos' },
    { id: 'channels', icon: Globe, label: 'Canais' },
  ];

  return (
    <div className="w-full max-w-[860px] mx-auto font-sans select-none">
      {/* MacBook Lid & Screen */}
      <div className="bg-[#D1D5DB] rounded-t-2xl border-[6px] border-[#374151] relative overflow-hidden">
        {/* Camera */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-3 bg-[#374151] rounded-b-lg z-50 flex items-center justify-center">
          <div className="w-1.5 h-1.5 rounded-full bg-[#4B5563]" />
        </div>

        {/* Browser chrome */}
        <div className="bg-[#F3F4F6] border-b border-[#E5E7EB] px-3 pt-3.5 pb-0 flex items-center gap-3">
          <div className="flex gap-1.5 shrink-0">
            <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#10B981]" />
          </div>
          <div className="flex-1 flex gap-2 overflow-x-hidden">
            <div className="bg-white border border-[#E5E7EB] rounded-t-lg px-3 py-1.5 text-[11px] font-medium text-slate-700 flex items-center gap-1.5 min-w-0 max-w-[220px]">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
              <span className="truncate">hub.youmachine.com.br</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-slate-400 shrink-0">
            <RefreshCw className="w-3.5 h-3.5" />
            <Download className="w-3.5 h-3.5" />
          </div>
        </div>

        {/* App viewport */}
        <div className="h-[440px] bg-white overflow-hidden flex">

          {/* ── LEFT SIDEBAR ── */}
          <div className="w-48 bg-[#FAFAFA] border-r border-[#F1F5F9] flex flex-col py-4 shrink-0">
            {/* Brand */}
            <div className="px-4 mb-5 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-[#0061FF] text-white font-black flex items-center justify-center text-xs">Y</div>
              <div>
                <span className="block text-[11px] font-black text-slate-900">YouMachine</span>
                <span className="block text-[9px] font-bold text-[#0061FF]">Genesis 360°</span>
              </div>
            </div>

            {/* Nav Items */}
            <nav className="flex-1 px-2 space-y-0.5">
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => setActiveNav(item.id)}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors ${
                    activeNav === item.id
                      ? 'bg-[#EFF6FF] text-[#0061FF]'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <item.icon className={`w-3.5 h-3.5 shrink-0 ${activeNav === item.id ? 'text-[#0061FF]' : 'text-slate-400'}`} />
                  <span className="text-[11px] font-semibold">{item.label}</span>
                  {item.id === 'orders' && (
                    <span className="ml-auto bg-[#0061FF] text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">3</span>
                  )}
                </button>
              ))}
            </nav>

            {/* Bottom divider */}
            <div className="mt-auto px-4 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 text-[9px] font-black flex items-center justify-center">A</div>
                <div>
                  <span className="block text-[10px] font-bold text-slate-900">Admin</span>
                  <span className="block text-[9px] text-slate-400">admin@youmachine</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── MAIN CONTENT ── */}
          <div className={`flex-1 overflow-y-auto bg-[#F8FAFC] ${autoScroll ? 'animate-[dashboardScroll_28s_ease-in-out_infinite]' : ''}`}>
            <div className="p-5 space-y-4">

              {/* Page Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-black text-slate-900">Painel Operacional</h2>
                  <p className="text-[10px] text-slate-400 font-medium">Terça-feira, 22 de Julho · Sync ao vivo</p>
                </div>
                <div className="flex items-center gap-2">
                  <button className="bg-white border border-slate-200 text-slate-600 text-[10px] font-bold px-2.5 py-1.5 rounded-lg flex items-center gap-1.5">
                    <Filter className="w-3 h-3" /> Filtrar
                  </button>
                  <button className="bg-[#0061FF] text-white text-[10px] font-bold px-2.5 py-1.5 rounded-lg flex items-center gap-1.5">
                    <Plus className="w-3 h-3" /> Novo Pedido
                  </button>
                </div>
              </div>

              {/* KPI Row */}
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: 'Faturamento Hoje', value: 'R$ 12.480', delta: '+18%', up: true, sub: 'vs. ontem' },
                  { label: 'Pedidos Ativos', value: '47', delta: '+8', up: true, sub: 'últimas 2h' },
                  { label: 'Ticket Médio', value: 'R$ 65,50', delta: '-3%', up: false, sub: 'vs. semana' },
                  { label: 'NPS Clientes', value: '94.2', delta: '+1.4', up: true, sub: 'pts este mês' },
                ].map((kpi, i) => (
                  <div key={i} className="bg-white border border-slate-200/80 rounded-xl p-3 space-y-1">
                    <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide block">{kpi.label}</span>
                    <strong className="text-base font-black text-slate-900 block leading-none">{kpi.value}</strong>
                    <div className="flex items-center gap-1">
                      {kpi.up
                        ? <ArrowUpRight className="w-3 h-3 text-emerald-500" />
                        : <ArrowDownRight className="w-3 h-3 text-rose-500" />}
                      <span className={`text-[9px] font-bold ${kpi.up ? 'text-emerald-600' : 'text-rose-600'}`}>{kpi.delta}</span>
                      <span className="text-[9px] text-slate-400">{kpi.sub}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Chart + Channels Row */}
              <div className="grid grid-cols-3 gap-3">
                {/* Bar Chart */}
                <div className="col-span-2 bg-white border border-slate-200/80 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-slate-900">Vendas por Hora — Hoje</span>
                    <span className="text-[9px] font-bold text-[#0061FF] bg-blue-50 px-2 py-0.5 rounded-md">Ao Vivo</span>
                  </div>
                  <div className="flex items-end gap-1 h-20 border-b border-slate-100 pb-1">
                    {CHART_BARS.map((h, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                        <div
                          className={`w-full rounded-t transition-all ${i === 6 ? 'bg-[#0061FF]' : 'bg-slate-200'}`}
                          style={{ height: `${h}%` }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between">
                    {CHART_LABELS.filter((_, i) => i % 2 === 0).map(l => (
                      <span key={l} className="text-[8px] text-slate-400 font-mono">{l}</span>
                    ))}
                  </div>
                </div>

                {/* Channel Share */}
                <div className="bg-white border border-slate-200/80 rounded-xl p-4 space-y-3">
                  <span className="text-[11px] font-bold text-slate-900 block">Canais de Venda</span>
                  <div className="space-y-2">
                    {[
                      { label: 'PWA App', pct: 54, color: 'bg-[#0061FF]' },
                      { label: 'WhatsApp', pct: 29, color: 'bg-emerald-500' },
                      { label: 'Google Maps', pct: 11, color: 'bg-amber-500' },
                      { label: 'Marketplaces', pct: 6, color: 'bg-purple-500' },
                    ].map(ch => (
                      <div key={ch.label} className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] font-medium text-slate-700">{ch.label}</span>
                          <span className="text-[10px] font-bold text-slate-900">{ch.pct}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${ch.color} rounded-full`} style={{ width: `${ch.pct}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Orders Table */}
              <div className="bg-white border border-slate-200/80 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <span className="text-[11px] font-bold text-slate-900">Pedidos Recentes</span>
                  <span className="text-[9px] font-bold text-[#0061FF] flex items-center gap-1 cursor-pointer hover:underline">
                    Ver todos <ChevronRight className="w-3 h-3" />
                  </span>
                </div>
                <table className="w-full text-left">
                  <thead className="bg-[#F8FAFC] border-b border-slate-100">
                    <tr>
                      {['Pedido', 'Cliente', 'Itens', 'Canal', 'Valor', 'Status', 'Hora'].map(h => (
                        <th key={h} className="px-3 py-2 text-[9px] font-bold text-slate-400 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ORDERS.map((o, i) => (
                      <tr key={i} className="border-b border-slate-100/80 hover:bg-slate-50/50 transition-colors">
                        <td className="px-3 py-2 text-[10px] font-bold text-slate-700">{o.id}</td>
                        <td className="px-3 py-2 text-[10px] font-medium text-slate-900">{o.customer}</td>
                        <td className="px-3 py-2 text-[10px] text-slate-600 max-w-[100px] truncate">{o.items}</td>
                        <td className="px-3 py-2">
                          <span className="text-[9px] font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">{o.channel}</span>
                        </td>
                        <td className="px-3 py-2 text-[10px] font-black text-slate-900">{o.value}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${o.statusColor}`}>{o.status}</span>
                        </td>
                        <td className="px-3 py-2 text-[9px] font-mono text-slate-400">{o.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </div>
          </div>

        </div>
      </div>

      {/* Aluminum base */}
      <div className="h-3 bg-[#9CA3AF] rounded-b-xl border-x border-b border-[#6B7280] flex justify-center items-center">
        <div className="w-16 h-1 bg-[#6B7280] rounded-full mt-0.5" />
      </div>
    </div>
  );
};
